import { SDK_Align, strings } from "@quickpoint.me/shared";
import { type ReactElement, memo, useContext } from "react";
import styled, { ThemeProvider } from "styled-components";
import { elementAlign, opacityHelper } from "../../../lib/cssHelpers";
import {
	CurrentFilter,
	FilterColors,
	FilterFonts,
} from "../../../state/filter";
import {
	PageElementsFamily,
	PageSizeAtom,
	createLocation,
} from "../../../state/page";

import { useRecoilValue } from "recoil";
import type { BulletProps } from "../../../@types/viewerati";
import type { ElementProps } from "../../../functionalComponents/RenderPage";
import { mainCssRules } from "../../../lib/cssTemplates";
import modifierStyled from "../../../lib/modifierStyled";
import { NeuroDivergentFontMultiplier } from "../../../state/accessibility";
import { ListRunContext } from "../../../lib/listRuns";
import { IsInFlexBoxContext } from "../../displayElements/Cell";

const StyledBullet = styled.div<
	BulletProps & {
		$fontMultiplier: number;
		$baseFontSizeRaw: number;
		$inListRun?: boolean;
	}
>`
  width: ${(props) => (props.$isInPortrait ? "100%" : "66%")}; // this aligns the bullets
  visibility: ${(props) => (props.$tapped ? "visible" : "hidden")};
  display: inline-flex;
  li span {
    position: relative;
    left: ${(props) => {
			const space =
				(props.theme.space ? props.theme.space / 10 : 0) * props.$baseFontSize;
			return `${space}px`;
		}}
  }

   ul {
    margin-block-end: 0;
    margin-block-start: 0;
    text-align: start;
    ${(props) =>
			props.theme.align !== "start" ? "padding-inline-start: 0;" : ""}
    li::marker {
      color: ${(props) => props.theme.markerColor};
    }
    li {
      span {
        text-wrap:  auto ;
      }
    }
  }

  ${(props) => elementAlign(props.theme.align, props.$isInFlexBox)};
  position: relative;
${(props) =>
	`${mainCssRules({
		baseFontSize: props.$baseFontSize,
		baseFontSizeRaw: props.$baseFontSizeRaw,
		fonts: props.$fonts,
		theme: props.theme,
		fontMultiplier: props.$fontMultiplier,
	})}`}
  opacity: ${(props) => opacityHelper(props.$tapped, props.theme.opacity)};
  ${(props) => `${modifierStyled("*:", props)}`}
  /* inside a list run the root IS the <li>: list-item display restores the
     marker the inline-flex default would suppress; the indent that the inner
     <ul> used to provide moves onto the item itself */
  ${(props) =>
		props.$inListRun
			? `
    display: list-item;
    width: 100%;
    margin-block: 0;
    padding-inline-start: 0;
    text-align: start;
    &::marker {
      color: ${props.theme.markerColor};
    }
    & > span {
      text-wrap: auto;
      position: relative;
      left: ${(props.theme.space ? props.theme.space / 10 : 0) * props.$baseFontSize}px;
    }
  `
			: ""}
`;

const Bullet = ({
	atomId,
	pageIndex,
}: ElementProps): ReactElement<ElementProps> => {
	const key = createLocation(pageIndex, atomId);
	const elementData = useRecoilValue(PageElementsFamily(key)).data;
	const isInFlexBox = useContext(IsInFlexBoxContext);
	const inListRun = useContext(ListRunContext);
	const filterColors = useRecoilValue(FilterColors);
	const fontMultiplier = useRecoilValue(NeuroDivergentFontMultiplier);
	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const tapped = elementData.tapped;
	let filter = useRecoilValue(CurrentFilter).bullet;

	const baseFontSize = pageSize.baseFontSize;

	if (!pageSize.landscape && filter.align !== SDK_Align.End) {
		filter = structuredClone(filter);
		filter.align = SDK_Align.Start;
	}
	if (!elementData.content) return <></>;

	return (
		<ThemeProvider theme={filter}>
			<StyledBullet
				as={inListRun ? "li" : undefined}
				$baseFontSize={baseFontSize}
				$baseFontSizeRaw={pageSize.baseFontSizeRaw}
				$elementIndex={atomId}
				$filterColors={filterColors}
				$fonts={fonts}
				$fontMultiplier={fontMultiplier}
				$inListRun={inListRun}
				$isInFlexBox={isInFlexBox}
				$modifier={elementData.modifier}
				$onePercentHeight={pageSize.onePercentHeight}
				$onePercentWidth={pageSize.onePercentWidth}
				$pageSize={pageSize}
				$isInPortrait={!pageSize.landscape}
				$tapped={tapped}
				className={`${strings.css.pageElement}  bullet`}
				data-atom-id={elementData.id}
			>
				{inListRun ? (
					<span>{elementData.content}</span>
				) : (
					<ul>
						<li>
							<span>{elementData.content}</span>
						</li>
					</ul>
				)}
			</StyledBullet>
		</ThemeProvider>
	);
};

export default memo(Bullet);
